import Phaser from "phaser";
import { Projectile } from "../entities/Projectile";
import { Tower } from "../entities/Tower";
import { Unit } from "../entities/Unit";
import { EconomySystem } from "./EconomySystem";
import { EventSystem } from "./EventSystem";
import { CounterAttackSystem } from "./CounterAttackSystem";
import mapConfig from "../data/mapConfig.json";

type DamageMultiplier = (target: Unit) => number;

export type CombatCallbacks = {
  onEnemyKilled?: (enemy: Unit, killer?: Unit) => void;
  onPlayerUnitKilled?: (unit: Unit) => void;
  onTowerDamaged?: (tower: Tower, attacker: Unit, damage: number) => void;
  onTowerDestroyed?: (tower: Tower, attacker: Unit) => void;
  onEnemySummon?: (summoner: Unit, enemyId: string) => void;
  onUnitConverted?: (unit: Unit, converter: Unit) => void;
  laneMorale?: (lane: Unit["lane"], faction: Unit["faction"]) => number;
  contactCombat?: boolean;
};

export class CombatSystem {
  readonly projectiles: Projectile[] = [];
  private readonly contactDistance = 44;
  private readonly encirclementRadius = 138;
  private readonly killCredits = new WeakMap<Unit, Unit>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly economy: EconomySystem,
    private readonly events: EventSystem,
    private readonly counter: CounterAttackSystem,
    private readonly callbacks: CombatCallbacks = {}
  ) {}

  prepareUnitMovement(towers: Tower[], playerUnits: Unit[], enemies: Unit[]) {
    if (this.callbacks.contactCombat === false) return;
    this.resolveLaneContacts(playerUnits, enemies);
    this.resolveWallContacts(towers, enemies);
  }

  update(time: number, deltaMs: number, towers: Tower[], playerUnits: Unit[], enemies: Unit[]) {
    for (let i = this.projectiles.length - 1; i >= 0; i -= 1) {
      const projectile = this.projectiles[i];
      if (!projectile) continue;
      projectile.update(deltaMs);
      if (!projectile.active) {
        this.projectiles.splice(i, 1);
      }
    }

    if (this.callbacks.contactCombat !== false) {
      this.resolveLaneContacts(playerUnits, enemies);
    }
    this.updateTowers(time, towers, enemies, playerUnits);
    this.updateUnits(time, towers, playerUnits, enemies);
    this.cleanupDead(playerUnits, enemies, towers);
  }

  private updateTowers(time: number, towers: Tower[], enemies: Unit[], playerUnits: Unit[]) {
    for (const tower of towers) {
      if (!tower.canAct(time)) continue;

      if (tower.healing) {
        const ally = this.selectTowerHealTarget(
          tower,
          playerUnits.filter((unit) => unit.alive && unit.hp < unit.maxHp && Phaser.Math.Distance.Between(tower.x, tower.y, unit.x, unit.y) <= tower.range)
        );
        if (ally) {
          ally.heal(Math.abs(tower.damage));
          tower.markAction(time);
        }
        continue;
      }

      const range = tower.range * this.events.rangeMultiplier;
      const target = this.selectTowerAttackTarget(
        tower,
        enemies.filter((enemy) => enemy.alive && Phaser.Math.Distance.Between(tower.x, tower.y, enemy.x, enemy.y) <= range)
      );

      if (target) {
        const damage = tower.id === "powder_trap" ? tower.damage * this.events.powderMultiplier : tower.damage;
        this.fireProjectile(
          tower.x,
          tower.y,
          target,
          enemies,
          damage,
          tower.splashRadius,
          tower.slow,
          0,
          tower.id === "ice_circle" ? "projectile_frost" : "projectile_player",
          false,
          (hitTarget) => this.moraleDamageTakenMultiplier(hitTarget, playerUnits) * this.terrainDamageTakenMultiplier(hitTarget, true)
        );
        tower.markAction(time);
      }
    }
  }

  private selectTowerHealTarget(tower: Tower, candidates: Unit[]) {
    if (candidates.length === 0) return undefined;
    const distance = (unit: Unit) => Phaser.Math.Distance.Between(tower.x, tower.y, unit.x, unit.y);
    if (tower.targetPriority === "nearest") {
      return [...candidates].sort((a, b) => distance(a) - distance(b))[0];
    }
    return [...candidates].sort((a, b) => a.hp / Math.max(1, a.maxHp) - b.hp / Math.max(1, b.maxHp) || distance(a) - distance(b))[0];
  }

  private selectTowerAttackTarget(tower: Tower, candidates: Unit[]) {
    if (candidates.length === 0) return undefined;
    const distance = (unit: Unit) => Phaser.Math.Distance.Between(tower.x, tower.y, unit.x, unit.y);
    const byNearest = (a: Unit, b: Unit) => distance(a) - distance(b);

    if (tower.targetPriority === "nearest") {
      return [...candidates].sort(byNearest)[0];
    }
    if (tower.targetPriority === "lowest_hp") {
      return [...candidates].sort((a, b) => a.hp / Math.max(1, a.maxHp) - b.hp / Math.max(1, b.maxHp) || byNearest(a, b))[0];
    }
    if (tower.targetPriority === "fast_first") {
      return [...candidates].sort((a, b) => b.baseSpeed - a.baseSpeed || byNearest(a, b))[0];
    }
    if (tower.targetPriority === "siege_first") {
      return [...candidates].sort((a, b) => this.towerEnemySiegeScore(b) - this.towerEnemySiegeScore(a) || this.towerEnemyThreatScore(b) - this.towerEnemyThreatScore(a) || byNearest(a, b))[0];
    }
    return [...candidates].sort((a, b) => this.towerEnemyThreatScore(b) - this.towerEnemyThreatScore(a) || byNearest(a, b))[0];
  }

  private towerEnemySiegeScore(enemy: Unit) {
    let score = 0;
    if (enemy.role === "siege" || enemy.role === "siege_beast") score += 80;
    if (enemy.targetPriority === "siege") score += 70;
    if (enemy.targetPriority === "towers") score += 42;
    if (enemy.targetPriority === "castle") score += 32;
    score += Math.max(0, enemy.castleDamage) * 2.2;
    score += Math.max(0, enemy.siegeDamageMultiplier - 1) * 18;
    return score;
  }

  private towerEnemyThreatScore(enemy: Unit) {
    const dps = Math.max(0, enemy.damage) / Math.max(0.25, enemy.attackRateMs / 1000);
    const splash = enemy.splashRadius > 0 ? 18 + enemy.splashRadius / 9 : 0;
    const control = enemy.slow > 0 ? 16 + enemy.slow * 20 : enemy.freezeChance > 0 ? 12 + enemy.freezeChance * 20 : 0;
    const siege = this.towerEnemySiegeScore(enemy) * 0.65;
    const speedPressure = Math.max(0, enemy.baseSpeed - 36) * 0.35;
    const lowHpFinish = enemy.hp / Math.max(1, enemy.maxHp) <= 0.28 ? 12 : 0;
    const summoner = enemy.summon ? 28 : 0;
    return dps + splash + control + siege + speedPressure + lowHpFinish + summoner;
  }

  private updateUnits(time: number, towers: Tower[], playerUnits: Unit[], enemies: Unit[]) {
    const laneBuffs = new Map<number, number>();
    for (const unit of playerUnits) {
      const aura = unit.alive ? unit.effectiveBuffAura(time) : 0;
      if (aura > 0) {
        laneBuffs.set(unit.lane, Math.max(laneBuffs.get(unit.lane) ?? 0, aura));
      }
    }

    for (const unit of playerUnits) {
      if (!unit.canAttack(time)) continue;

      if (unit.repairPower > 0 && this.tryRepairDefense(unit, towers, time)) {
        continue;
      }

      if (unit.damage < 0) {
        const ally = playerUnits
          .filter((candidate) => candidate.alive && candidate.lane === unit.lane && candidate.hp < candidate.maxHp)
          .sort((a, b) => Phaser.Math.Distance.Between(unit.x, unit.y, a.x, a.y) - Phaser.Math.Distance.Between(unit.x, unit.y, b.x, b.y))[0];
        if (ally && Phaser.Math.Distance.Between(unit.x, unit.y, ally.x, ally.y) <= unit.range) {
          ally.heal(Math.abs(unit.damage));
          unit.markAttack(time);
        }
        continue;
      }

      if (unit.id === "alchemist" && this.tryAlchemistHeal(unit, playerUnits, time)) {
        continue;
      }

      const target = enemies
        .filter((enemy) => enemy.alive && enemy.lane === unit.lane)
        .sort((a, b) => a.x - b.x)
        .find((enemy) => Phaser.Math.Distance.Between(unit.x, unit.y, enemy.x, enemy.y) <= Math.max(unit.range, this.contactDistance + 8));

      if (!target) continue;
      const contactAttack = this.isContactAttack(unit, target);
      const damage =
        unit.damage *
        (this.counter.active ? 1.35 : 1) *
        (1 + (laneBuffs.get(unit.lane) ?? 0)) *
        this.castleTerritoryMultiplier(unit) *
        this.moraleAttackMultiplier(unit, playerUnits, enemies, target) *
        this.laneMoraleAttackMultiplier(unit) *
        this.terrainAttackMultiplier(unit, !contactAttack);
      if (contactAttack) {
        this.applyHit(
          target,
          enemies,
          damage,
          unit.splashRadius,
          unit.slow,
          unit.freezeChance,
          true,
          (hitTarget) => this.moraleDamageTakenMultiplier(hitTarget, playerUnits) * this.terrainDamageTakenMultiplier(hitTarget, false),
          unit
        );
      } else {
        this.fireProjectile(
          unit.x,
          unit.y,
          target,
          enemies,
          damage,
          unit.splashRadius,
          unit.slow,
          unit.freezeChance,
          unit.slow > 0 ? "projectile_frost" : "projectile_player",
          true,
          (hitTarget) => this.moraleDamageTakenMultiplier(hitTarget, playerUnits) * this.terrainDamageTakenMultiplier(hitTarget, true),
          unit
        );
      }
      unit.markAttack(time);
    }

    for (const enemy of enemies) {
      if (!enemy.canAttack(time)) continue;

      if (enemy.summon && time - enemy.lastSummonAt > 5200) {
        enemy.lastSummonAt = time;
        enemy.markAttack(time);
        this.callbacks.onEnemySummon?.(enemy, enemy.summon);
        continue;
      }

      if (enemy.isRetreating() && !this.isRetreatContact(enemy, playerUnits)) {
        continue;
      }

      const towerTarget = this.findEnemyTowerTarget(enemy, towers);
      if (towerTarget) {
        const damage = Math.round(enemy.damage * (enemy.targetPriority === "siege" ? enemy.siegeDamageMultiplier : 1) * this.castleTerritoryMultiplier(enemy));
        towerTarget.takeDamage(damage);
        this.spawnHitSparkAt(towerTarget.x, towerTarget.y - 8);
        this.callbacks.onTowerDamaged?.(towerTarget, enemy, damage);
        if (towerTarget.destroyed) {
          this.callbacks.onTowerDestroyed?.(towerTarget, enemy);
        }
        enemy.markAttack(time);
        continue;
      }

      const target = playerUnits
        .filter((unit) => unit.alive && unit.lane === enemy.lane)
        .sort((a, b) => b.x - a.x)
        .find((unit) => Phaser.Math.Distance.Between(enemy.x, enemy.y, unit.x, unit.y) <= Math.max(enemy.range, this.contactDistance + 8));

      if (!target) continue;
      const contactAttack = this.isContactAttack(enemy, target);
      const damage =
        enemy.damage *
        this.castleTerritoryMultiplier(enemy) *
        this.moraleAttackMultiplier(enemy, enemies, playerUnits, target) *
        this.laneMoraleAttackMultiplier(enemy) *
        this.terrainAttackMultiplier(enemy, !contactAttack);
      if (contactAttack) {
        this.applyHit(
          target,
          playerUnits,
          damage,
          enemy.splashRadius,
          enemy.slow,
          0,
          true,
          (hitTarget) => this.moraleDamageTakenMultiplier(hitTarget, enemies) * this.terrainDamageTakenMultiplier(hitTarget, false),
          enemy
        );
      } else {
        this.fireProjectile(
          enemy.x,
          enemy.y,
          target,
          playerUnits,
          damage,
          enemy.splashRadius,
          enemy.slow,
          0,
          "projectile_enemy",
          true,
          (hitTarget) => this.moraleDamageTakenMultiplier(hitTarget, enemies) * this.terrainDamageTakenMultiplier(hitTarget, true),
          enemy
        );
      }
      if (enemy.dot) {
        this.applyDamageOverTime(target, enemy.dot.damage, enemy.dot.durationMs);
      }
      if (enemy.convertChance > 0 && Phaser.Math.FloatBetween(0, 1) <= enemy.convertChance) {
        target.sprite.setTint(0x9aa0a6);
        target.takeDamage(target.hp);
        this.callbacks.onUnitConverted?.(target, enemy);
      }
      enemy.markAttack(time);
    }
  }

  private tryRepairDefense(unit: Unit, towers: Tower[], time: number) {
    const tower = towers
      .filter((candidate) => !candidate.destroyed && candidate.lane === unit.lane && candidate.hp < candidate.maxHp)
      .sort((a, b) => Phaser.Math.Distance.Between(unit.x, unit.y, a.x, a.y) - Phaser.Math.Distance.Between(unit.x, unit.y, b.x, b.y))[0];
    if (!tower || Phaser.Math.Distance.Between(unit.x, unit.y, tower.x, tower.y) > unit.range) return false;
    tower.heal(unit.repairPower);
    this.spawnHitSparkAt(tower.x, tower.y - 8);
    unit.markAttack(time);
    return true;
  }

  private tryAlchemistHeal(unit: Unit, playerUnits: Unit[], time: number) {
    const ally = playerUnits
      .filter((candidate) => candidate.alive && candidate.lane === unit.lane && candidate.hp < candidate.maxHp && candidate !== unit)
      .sort((a, b) => Phaser.Math.Distance.Between(unit.x, unit.y, a.x, a.y) - Phaser.Math.Distance.Between(unit.x, unit.y, b.x, b.y))[0];
    if (!ally || Phaser.Math.Distance.Between(unit.x, unit.y, ally.x, ally.y) > unit.range) return false;
    ally.heal(12);
    this.spawnHitSpark(ally);
    unit.markAttack(time);
    return true;
  }

  private fireProjectile(
    x: number,
    y: number,
    target: Unit,
    targets: Unit[],
    damage: number,
    splashRadius: number,
    slow: number,
    freezeChance: number,
    texture: string,
    laneRestricted = true,
    damageMultiplier?: DamageMultiplier,
    source?: Unit
  ) {
    const projectile = new Projectile(this.scene, {
      x,
      y,
      target,
      damage,
      texture,
      splashRadius,
      slow,
      onHit: (hitTarget, firedProjectile) => {
        this.applyHit(hitTarget, targets, firedProjectile.damage, firedProjectile.splashRadius, firedProjectile.slow, freezeChance, laneRestricted, damageMultiplier, source);
      }
    });
    this.projectiles.push(projectile);
  }

  private resolveLaneContacts(playerUnits: Unit[], enemies: Unit[]) {
    for (const unit of [...playerUnits, ...enemies]) {
      unit.setMovementBlocked(false);
    }

    for (const lane of [1, 2, 3, 4, 5] as const) {
      const lanePlayers = playerUnits.filter((unit) => unit.alive && unit.lane === lane).sort((a, b) => a.x - b.x);
      const laneEnemies = enemies.filter((unit) => unit.alive && unit.lane === lane).sort((a, b) => b.x - a.x);
      const usedEnemies = new Set<Unit>();

      for (const player of lanePlayers) {
        const enemy = laneEnemies.find((candidate) => !usedEnemies.has(candidate) && Math.abs(player.x - candidate.x) <= this.contactDistance);
        if (!enemy) continue;

        usedEnemies.add(enemy);
        const midpoint = (player.x + enemy.x) / 2;
        const halfGap = this.contactDistance / 2;
        enemy.setX(midpoint - halfGap);
        player.setX(midpoint + halfGap);
        enemy.setMovementBlocked(true);
        player.setMovementBlocked(true);
      }
    }
  }

  private resolveWallContacts(towers: Tower[], enemies: Unit[]) {
    const activeWalls = towers.filter((tower) => tower.blocker && !tower.destroyed && !tower.consumed);
    for (const enemy of enemies) {
      const wall = activeWalls
        .filter((tower) => tower.lane === enemy.lane && enemy.x >= tower.x - this.contactDistance && enemy.x <= tower.x + this.contactDistance + 18)
        .sort((a, b) => a.x - b.x)[0];
      if (!wall) continue;
      enemy.setX(wall.x - this.contactDistance);
      enemy.setMovementBlocked(true);
    }
  }

  private findEnemyTowerTarget(enemy: Unit, towers: Tower[]) {
    const laneTowers = towers.filter((tower) => !tower.destroyed && !tower.consumed && tower.lane === enemy.lane);
    const wall = laneTowers
      .filter((tower) => tower.blocker && enemy.x <= tower.x + this.contactDistance)
      .sort((a, b) => a.x - b.x)[0];
    if (wall && Phaser.Math.Distance.Between(enemy.x, enemy.y, wall.x, wall.y) <= Math.max(enemy.range, this.contactDistance + 16)) {
      enemy.setMovementBlocked(true);
      return wall;
    }

    if (enemy.targetPriority !== "towers" && enemy.targetPriority !== "siege") return undefined;
    return laneTowers
      .filter((tower) => Phaser.Math.Distance.Between(enemy.x, enemy.y, tower.x, tower.y) <= Math.max(enemy.range, this.contactDistance + 20))
      .sort((a, b) => this.towerTargetScore(b, enemy) - this.towerTargetScore(a, enemy) || Phaser.Math.Distance.Between(enemy.x, enemy.y, a.x, a.y) - Phaser.Math.Distance.Between(enemy.x, enemy.y, b.x, b.y))[0];
  }

  private towerTargetScore(tower: Tower, enemy: Unit) {
    const dps = Math.max(0, tower.damage) / Math.max(0.25, tower.attackRateMs / 1000);
    const support = tower.healing ? Math.abs(tower.damage) / Math.max(0.25, tower.attackRateMs / 1000) * 1.4 : 0;
    const splash = tower.splashRadius > 0 ? 16 + tower.splashRadius / 10 : 0;
    const slow = tower.slow > 0 ? 8 + tower.slow * 16 : 0;
    const range = Math.max(0, tower.range - 170) / 10;
    const wall = tower.blocker ? 22 : 0;
    const scout = tower.scoutCoverage ? 4 : 0;
    const lowHpFinish = tower.hp / Math.max(1, tower.maxHp) <= 0.35 ? 10 : 0;
    const reachableSoon = Math.max(0, 12 - Phaser.Math.Distance.Between(enemy.x, enemy.y, tower.x, tower.y) / 18);
    return dps + support + splash + slow + range + wall + scout + lowHpFinish + reachableSoon;
  }

  private isContactAttack(attacker: Unit, target: Unit) {
    return attacker.range <= 52 || Phaser.Math.Distance.Between(attacker.x, attacker.y, target.x, target.y) <= this.contactDistance + 8;
  }

  private isRetreatContact(enemy: Unit, playerUnits: Unit[]) {
    return playerUnits.some(
      (unit) => unit.alive && unit.lane === enemy.lane && Phaser.Math.Distance.Between(enemy.x, enemy.y, unit.x, unit.y) <= this.contactDistance + 8
    );
  }

  private castleTerritoryMultiplier(unit: Unit) {
    if (unit.faction === "player" && unit.x >= mapConfig.playerCastleX - 220) return 1.25;
    if (unit.faction === "enemy" && unit.x <= mapConfig.aiFortressX + 220) return 1.25;
    return 1;
  }

  private terrainForLane(lane: Unit["lane"]) {
    return mapConfig.lanes.find((item) => item.lane === lane)?.terrain ?? "cobblestone";
  }

  private terrainAttackMultiplier(attacker: Unit, ranged: boolean) {
    let multiplier = 1;
    const attackerTerrain = this.terrainForLane(attacker.lane);
    if (!ranged && attackerTerrain === "ruins") multiplier *= 1.06;
    if (attackerTerrain === "marsh") multiplier *= 0.95;
    return multiplier;
  }

  private terrainDamageTakenMultiplier(target: Unit, ranged: boolean) {
    let multiplier = 1;
    const terrain = this.terrainForLane(target.lane);
    if (ranged && terrain === "forest") multiplier *= 0.88;
    if (terrain === "wall_road") multiplier *= 0.94;
    return multiplier;
  }

  private moraleAttackMultiplier(attacker: Unit, allies: Unit[], opponents: Unit[], target: Unit) {
    let multiplier = 1;
    if (this.isEncircledBy(attacker, opponents)) {
      multiplier *= 0.55;
    }
    if (this.isEncircledBy(target, allies)) {
      multiplier *= 1.18;
    }
    return multiplier;
  }

  private moraleDamageTakenMultiplier(target: Unit, attackers: Unit[]) {
    const moraleMultiplier = this.isEncircledBy(target, attackers) ? 1.48 : 1;
    return moraleMultiplier * this.laneMoraleDamageTakenMultiplier(target) * target.objectiveDamageTakenMultiplier(this.scene.time.now);
  }

  private laneMoraleAttackMultiplier(unit: Unit) {
    const morale = this.callbacks.laneMorale?.(unit.lane, unit.faction) ?? 60;
    if (morale < 30) return 0.8;
    if (morale < 50) return 0.9;
    if (morale >= 80) return 1.12;
    if (morale >= 70) return 1.06;
    return 1;
  }

  private laneMoraleDamageTakenMultiplier(unit: Unit) {
    const morale = this.callbacks.laneMorale?.(unit.lane, unit.faction) ?? 60;
    if (morale < 30) return 1.22;
    if (morale < 50) return 1.1;
    if (morale >= 80) return 0.92;
    if (morale >= 70) return 0.96;
    return 1;
  }

  private isEncircledBy(unit: Unit, opponents: Unit[]) {
    const nearby = opponents.filter(
      (opponent) => opponent.alive && opponent.lane === unit.lane && Math.abs(opponent.x - unit.x) <= this.encirclementRadius
    );
    if (nearby.length < 2) return false;
    const hasLeft = nearby.some((opponent) => opponent.x < unit.x - 18);
    const hasRight = nearby.some((opponent) => opponent.x > unit.x + 18);
    return hasLeft && hasRight;
  }

  private applyHit(
    primaryTarget: Unit,
    targets: Unit[],
    damage: number,
    splashRadius: number,
    slow: number,
    freezeChance: number,
    laneRestricted = true,
    damageMultiplier?: DamageMultiplier,
    source?: Unit
  ) {
    const hitTargets =
      splashRadius > 0
        ? targets.filter(
            (target) =>
              target.alive &&
              (!laneRestricted || target.lane === primaryTarget.lane) &&
              Phaser.Math.Distance.Between(primaryTarget.x, primaryTarget.y, target.x, target.y) <= splashRadius
          )
        : [primaryTarget];

    for (const target of hitTargets) {
      const moraleMultiplier = damageMultiplier?.(target) ?? 1;
      const siegeMultiplier = this.siegeDamageTakenMultiplier(target, source, moraleMultiplier);
      const wasAlive = target.alive;
      target.takeDamage(damage * moraleMultiplier * siegeMultiplier);
      if (wasAlive && !target.alive && source?.faction === "player" && target.faction === "enemy") {
        this.killCredits.set(target, source);
      }
      if (slow > 0) {
        target.applySlow(slow, this.scene.time.now + 1800);
      }
      if (moraleMultiplier > 1.05) {
        target.sprite.setTint(0xffb15a);
        this.scene.time.delayedCall(150, () => {
          if (target.alive) target.sprite.clearTint();
        });
      } else if (siegeMultiplier > 1.05) {
        target.sprite.setTint(0xffd36e);
        this.scene.time.delayedCall(150, () => {
          if (target.alive) target.sprite.clearTint();
        });
      }
      if (freezeChance > 0 && Phaser.Math.FloatBetween(0, 1) <= freezeChance) {
        target.applySlow(0.95, this.scene.time.now + 950);
        target.sprite.setTint(0x9feaff);
        this.scene.time.delayedCall(240, () => {
          if (target.alive) target.sprite.clearTint();
        });
      }
      this.spawnHitSpark(target);
    }
  }

  private siegeDamageTakenMultiplier(target: Unit, source: Unit | undefined, moraleMultiplier: number) {
    if (target.role !== "siege" && target.role !== "siege_beast") return 1;
    const melee = source ? this.isContactAttack(source, target) : false;
    let multiplier = melee ? 1.35 : 0.9;
    if (target.isDeploying(this.scene.time.now)) multiplier *= 1.2;
    if (moraleMultiplier >= 1.35) multiplier *= 1.2;
    return multiplier;
  }

  private spawnHitSpark(target: Unit) {
    this.spawnHitSparkAt(target.x, target.y - 8);
  }

  private spawnHitSparkAt(x: number, y: number) {
    const spark = this.scene.add.image(x, y, "hit_spark").setDepth(86).setScale(0.45).setAlpha(0.95);
    this.scene.tweens.add({
      targets: spark,
      alpha: 0,
      scale: 0.85,
      duration: 180,
      onComplete: () => spark.destroy()
    });
  }

  private applyDamageOverTime(target: Unit, damage: number, durationMs: number) {
    const ticks = Math.max(1, Math.floor(durationMs / 900));
    for (let tick = 1; tick <= ticks; tick += 1) {
      this.scene.time.delayedCall(tick * 900, () => {
        if (!target.alive) return;
        target.takeDamage(damage);
        this.spawnHitSpark(target);
      });
    }
  }

  private cleanupDead(playerUnits: Unit[], enemies: Unit[], towers: Tower[]) {
    for (let index = enemies.length - 1; index >= 0; index -= 1) {
      const enemy = enemies[index];
      if (enemy && !enemy.alive) {
        if (enemy.explodeOnDeath) {
          for (const target of playerUnits) {
            if (target.alive && target.lane === enemy.lane && Phaser.Math.Distance.Between(enemy.x, enemy.y, target.x, target.y) <= enemy.explodeOnDeath.radius) {
              target.takeDamage(enemy.explodeOnDeath.damage);
              this.spawnHitSpark(target);
            }
          }
          for (const tower of towers) {
            if (!tower.destroyed && tower.lane === enemy.lane && Phaser.Math.Distance.Between(enemy.x, enemy.y, tower.x, tower.y) <= enemy.explodeOnDeath.radius) {
              tower.takeDamage(enemy.explodeOnDeath.damage);
              this.spawnHitSparkAt(tower.x, tower.y - 8);
            }
          }
        }
        enemies.splice(index, 1);
        this.economy.reward(enemy.goldReward);
        this.counter.add(5);
        this.callbacks.onEnemyKilled?.(enemy, this.killCredits.get(enemy));
      }
    }

    for (let index = playerUnits.length - 1; index >= 0; index -= 1) {
      const unit = playerUnits[index];
      if (unit && !unit.alive) {
        playerUnits.splice(index, 1);
        this.callbacks.onPlayerUnitKilled?.(unit);
      }
    }
  }
}
